import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Button,
  InputAdornment,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  Select,
  Chip,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import Drawer from "@mui/material/Drawer";
import { DataGrid } from "@mui/x-data-grid";
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { deleteOrder, getAllCakeOrders } from "../../service/orderService";
import { useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Color from "../../components/Color";
import { downloadOrderPDF } from "../../service/orderService";
import { useSnackbar } from "../../App";

const CakeOrderLists = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [rowCount, setRowCount] = useState(0);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [sortModel, setSortModel] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [dateErrors, setDateErrors] = useState({ order_date_from: "", order_date_to: "" });

  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const { showSnackbar } = useSnackbar();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down("xs"));

  const statusColors = {
    order_placed: { backgroundColor: "#2196F3", color: "#ffffff" },
    ready_for_delivery: { backgroundColor: "#FFCA28", color: "#000000" },
    completed: { backgroundColor: "#4CAF50", color: "#ffffff" },
    cancelled: { backgroundColor: "#D32F2F", color: "#ffffff" }, request_for_delete: { backgroundColor: "#ff5e00ff", color: "#ffffff" },
  };

  const statusOptions = [
    { value: "order_placed", label: "Order Placed" },
    { value: "ready_for_delivery", label: "Ready for Delivery" },
    { value: "completed", label: "Completed" },
    { value: "cancelled", label: "Cancelled" },
    { value: "request_for_delete", label: "Request for Delete"}
  ];
  const orderTypeOptions = [
    { value: "quotation", label: "Quotation" },
    { value: "invoice", label: "Invoice" },
  ];
  const paymentStatusOptions = [
    { value: "Paid", label: "Paid" },
    { value: "Unpaid", label: "Unpaid" },
    { value: "Partial", label: "Partial" },
  ];

  const initialFilterState = {
    order_number: "",
    status: "",
    order_type: "",
    payment_status: "",
    customer_id: "",
    customer_name: "",
    branch_id: "",
    branch_name: "",
    order_date_from: null,
    order_date_to: null,
    total_amount_min: "",
    total_amount_max: "",
    notes: "",
    sort_field: "",
    sort_order: "",
  };

  const [filters, setFilters] = useState(initialFilterState);

  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const validateDates = () => {
    const errors = {};
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (filters.order_date_from && filters.order_date_from > today) {
      errors.order_date_from = "From Date cannot be in the future";
    }
    if (filters.order_date_to && filters.order_date_to > today) {
      errors.order_date_to = "To Date cannot be in the future";
    }
    if (filters.order_date_from && filters.order_date_to) {
      if (filters.order_date_from > filters.order_date_to) {
        errors.order_date_to = "To Date must be on or after From Date";
      }
    }
    setDateErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = ({ target: { name, value } }) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    if (name === "customer_name") {
      setSearch(value); 
    }
    if (name === "order_date_from" || name === "order_date_to") {
      setDateErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (!validateDates()) {
      showSnackbar("Please fix date errors before searching.", "error");
      return;
    }
    setFiltersApplied(true);
    setDrawerOpen(false);
    fetchOrders();
  };

  const handleReset = () => {
    setFilters(initialFilterState);
    setSearch("");
    setDebouncedSearch("");
    setDateErrors({ order_date_from: "", order_date_to: "" });
    setPaginationModel({ page: 0, pageSize: 10 });
    setFiltersApplied(true);
    fetchOrders();
  };

  const handleClear = () => {
    setSearch("");
    setDebouncedSearch("");
    setFilters((prev) => ({ ...prev, customer_name: "" }));
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    fetchOrders();
  };

  const navigate = useNavigate();

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const page = paginationModel.page + 1;
      const limit = paginationModel.pageSize;
      const searchPayload = {
        ...filters,
        customer_name: debouncedSearch || filters.customer_name,
        category_id: 92,
        order_date_from: formatDate(filters.order_date_from),
        order_date_to: formatDate(filters.order_date_to),
        sort_field: sortModel.length > 0 ? sortModel[0].field : "id",
        sort_order: sortModel.length > 0 ? sortModel[0].sort : "desc",
      };
      const res = await getAllCakeOrders({ ...searchPayload, page, limit });

      const data = res?.data?.data;

      if (Array.isArray(data?.data)) {
        const normalizedRows = data.data.map((row) => ({
          ...row,
          customerName: row.customer?.name || "",
          categoryName: row.category?.name || "Unknown",
          branchName: row.branch?.name || "",
          order_date: row.order_date ? row.order_date.split("T")[0] : "",
          delivery_date: row.delivery_date ? row.delivery_date.split("T")[0] : "",
        }));
        setRows(normalizedRows);
        setRowCount(data.total);
        if ((debouncedSearch || filters.customer_name || filters.branch_name) && data.data.length === 0) {
          showSnackbar("No orders found for the search term or filters.", "info");
        }
      } else {
        setRows([]);
        showSnackbar("Unexpected data format!", "error");
      }
    } catch (error) {
      console.error("Fetch orders error:", error);
      showSnackbar(error?.response?.data?.message || "Failed to fetch orders!", "error");
    }
    setLoading(false);
  }, [filters, paginationModel, debouncedSearch, sortModel, showSnackbar]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const handleMenuOpen = (event, row) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteOrder(deleteId);
      showSnackbar("Order deleted successfully!", "success");
      fetchOrders();
    } catch (error) {
      console.error("Delete Order Error:", error);
      showSnackbar(error?.response?.data?.message || "Failed to delete order!", "error");
    } finally {
      setDeleteDialogOpen(false);
      setDeleteId(null);
    }
  };

  const handleDownloadPDF = async (id) => {
    try {
      const response = await downloadOrderPDF(id);
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `order_${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download PDF Error:", error);
      showSnackbar(error?.response?.data?.message || "Failed to download PDF.", "error");
    }
  };

  const columns = [
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.8,
      sortable: false,
      filterable: false,
      minWidth: isMobile ? 80 : 120,
      renderCell: (params) => (
        <IconButton
          onClick={(e) => handleMenuOpen(e, params.row)}
          sx={{
            backgroundColor: "rgba(0,0,0,0.04)",
            borderRadius: 1.5,
            padding: isMobile ? "10px" : "8px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.08)",
            },
          }}
        >
          <MoreVertIcon sx={{ fontSize: isMobile ? 18 : 16 }} />
        </IconButton>
      ),
    },
    { field: "order_number", headerName: "Order No", flex: 1, minWidth: isMobile ? 100 : 150 },
    { field: "customerName", headerName: "Customer", flex: 1, minWidth: isMobile ? 120 : 150 },
    { field: "branchName", headerName: "Branch", flex: 1.3, minWidth: 160 },
    { field: "order_date", headerName: "Order Date", flex: 1, minWidth: 180 },
    { field: "total_amount", headerName: "Total", flex: 1, minWidth: isMobile ? 80 : 120 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      minWidth: isMobile ? 140 : 120,
      renderCell: (params) => {
        const status = params.value;
        const { backgroundColor, color } = statusColors[status] || {
          backgroundColor: "#E0E0E0",
          color: "#000000",
        };
        return (
          <Chip
            label={
              statusOptions.find((opt) => opt.value === status)?.label || status
            }
            sx={{
              backgroundColor,
              color,
              fontSize: isMobile ? "0.75rem" : "0.85rem",
              fontWeight: 500,
              borderRadius: "12px",
            }}
          />
        );
      },
    },
  ];

  return (
    <Box mb={{ xs: 0, sm: 1 }} sx={{ padding: isMobile ? 1 : 0 }}>
      <Box
        display="flex"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
        gap={isMobile ? 1 : 2}
        mt={2}
      >
        <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: isMobile ? 1 : "10px", width: isMobile ? "100%" : "auto" }}>
          <TextField
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setFilters((prev) => ({ ...prev, customer_name: e.target.value }));
            }}
            placeholder="Search "
            variant="outlined"
            InputLabelProps={{ shrink: false }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ fontSize: isMobile ? 18 : 20 }} />
                </InputAdornment>
              ),
              endAdornment: search && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleClear}
                  >
                    <CloseIcon sx={{ fontSize: isMobile ? 16 : 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiInputLabel-root": { display: "none" },
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                height: isMobile ? "34px" : "37.69px",
                fontSize: isMobile ? "0.85rem" : "1rem",
              },
              width: isMobile ? "100%" : 250,
              mb: isMobile ? 0 : 0,
            }}
          />
          {/* <Button
            onClick={handleClear}
            sx={{
              height: isMobile ? "34px" : "36px",
              background: "none",
              width: isMobile ? "100%" : 90,
              color: Color.secondary.main,
              border: "1px solid",
              borderColor: Color.secondary.main,
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Clear
          </Button> */}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1 : 2 }}>
          <IconButton
            sx={{
              color: Color.secondary.main,
              fontSize: isMobile ? "26px" : "30px",
              border: "1px solid",
              borderColor: Color.secondary.main,
              borderRadius: "4px",
              height: isMobile ? "34px" : "36px",
              width: isMobile ? "34px" : "36px",
            }}
            onClick={() => setDrawerOpen(true)}
          >
            <FilterAltIcon sx={{ fontSize: isMobile ? 20 : 24 }} />
          </IconButton>
          <Button
            sx={{
              background: Color.primary.main,
              color: "white",
              height: isMobile ? "34px" : "37.69px",
              width: isMobile ? "100%" : 200,
              fontSize: isMobile ? "0.85rem" : "1rem",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
            onClick={() => navigate("/cakeorder")}
            startIcon={<AddIcon />}
          >
            Create Cake Order
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%", mt: isMobile ? 1 : 2 }}>
        <DataGrid
          rows={rows.filter(
            (row) =>
              row.customerName?.toLowerCase().includes(search.toLowerCase()) ||
              row.order_number?.toLowerCase().includes(search.toLowerCase()) ||
              row.branchName?.toLowerCase().includes(search.toLowerCase())
          )}
          columns={columns}
          rowHeight={isMobile ? 48 : 56}
          loading={loading}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          paginationModel={paginationModel}
          paginationMode="server"
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          onPaginationModelChange={setPaginationModel}
          disableRowSelectionOnClick
          sx={{
            fontFamily: '"Poppins", sans-serif',
            borderRadius: "10px",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontSize: isMobile ? "14px" : "18px",
              fontWeight: 500,
              pl: 1,
            },
            "& .MuiDataGrid-cell": {
              fontSize: isMobile ? "12px" : "14px",
              pl: 2,
            },
            "& .MuiDataGrid-row:nth-of-type(even)": {
              backgroundColor: "#fff",
            },
            "& .MuiDataGrid-row:nth-of-type(odd)": {
              backgroundColor: "#f4f4f4",
            },
            "& .MuiLinearProgress-bar": {
              backgroundColor: Color.primary.main,
            },
            "& .MuiLinearProgress-colorPrimary": {
              backgroundColor: Color.secondary.main,
            },
            overflowX: isMobile ? "hidden" : "auto",
            "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
            "& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within": {
              outline: "none",
              border: "2px solid",
              borderColor: Color.primary.main,
              borderRadius: "4px",
            },
          }}
        />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleMenuClose}
        PaperProps={{
          style: {
            maxHeight: 200,
            width: isMobile ? "90vw" : 200,
          },
        }}
      >
        <MenuItem
          onClick={() => navigate(`/edit-cake-order/${selectedRow?.id}`)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => handleDelete(selectedRow?.id)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1, color: "error.main" }} /> Delete
        </MenuItem>
        <MenuItem
          onClick={() => handleDownloadPDF(selectedRow?.id)}
          sx={{ fontSize: isMobile ? "0.85rem" : "1rem", padding: isMobile ? "10px" : "8px" }}
        >
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} /> PDF
        </MenuItem>
      </Menu>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={(event, reason) => {
          if (reason !== "backdropClick") {
            setDrawerOpen(false);
          }
        }}
        PaperProps={{
          sx: {
            width: isMobile ? "60vw" : 480,
            boxShadow: 4,
            borderRadius: "8px 0 0 8px",
          },
        }}
        sx={{
          zIndex: 1300,
        }}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(129, 128, 128, 0.05)",
            backdropFilter: "blur(2px)",
          },
        }}
      >
        <Box
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            fontFamily: '"Poppins", sans-serif',
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            py={1}
            mt={isMobile ? 1 : 1}
            borderBottom="1px solid #e0e0e0"
          >
            <Typography fontSize={isMobile ? 16 : 18} fontWeight={600} sx={{ mt: { xs: 2, md: 0 } }}>
              Filter Orders
            </Typography>
            <IconButton onClick={() => setDrawerOpen(false)} size="small">
              <CloseIcon sx={{ fontSize: isMobile ? 20 : 24, mt: { xs: 2, md: 0 } }} />
            </IconButton>
          </Box>

          <Box p={isMobile ? 2 : 2} sx={{ flexGrow: 1, overflowY: "auto" }}>
            <form onSubmit={handleSearch}>
              <Grid container spacing={isMobile ? 1 : 2}>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                   Search (All)
                  </Typography>
                  <TextField
                    fullWidth
                    name="customer_name"
                    value={filters.customer_name}
                    onChange={handleChange}
                    placeholder="Search name, branch"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "100%" : "215px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Order Number
                  </Typography>
                  <TextField
                    fullWidth
                    name="order_number"
                    value={filters.order_number}
                    onChange={handleChange}
                    placeholder="Search order number"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    sx={{
                      "& .MuiInputLabel-root": { display: "none" },
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "100%" : "215px",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    sx={{
                      mb: 0.5,
                      fontWeight: 600,
                      color: Color.label.main,
                      fontSize: isMobile ? "12px" : "13px",
                    }}
                  >
                    Order Status
                  </Typography>
                  <FormControl
                    fullWidth
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                        height: isMobile ? "34px" : "37.69px",
                        fontSize: isMobile ? "0.85rem" : "1rem",
                        width: isMobile ? "105%" : "215px",
                      },
                    }}
                  >
                    <Select
                      name="status"
                      value={filters.status}
                      onChange={handleChange}
                      displayEmpty
                    >
                      <MenuItem value="" disabled>
                        Select Order Status
                      </MenuItem>
                      {statusOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={isExtraSmall ? 12 : 6}>
                    <Typography
                      sx={{
                        mb: 0.5,
                        fontWeight: 600,
                        color: Color.label.main,
                        fontSize: isMobile ? "12px" : "13px",
                      }}
                    >
                      Order Date(From)
                    </Typography>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        value={filters.order_date_from}
                        onChange={(newValue) =>
                          handleChange({
                            target: { name: "order_date_from", value: newValue },
                          })
                        }
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            variant: "outlined",
                            InputLabelProps: { shrink: false },
                            size: "small",
                            sx: {
                              "& .MuiInputLabel-root": { display: "none" },
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                                height: isMobile ? "34px" : "37.69px",
                                fontSize: isMobile ? "0.85rem" : "1rem",
                              },
                              width: isMobile ? "100%" : "215px",
                            },
                            error: !!dateErrors.order_date_from,
                            helperText: dateErrors.order_date_from,
                          },
                        }}
                        format="yyyy-MM-dd"
                      />
                    </LocalizationProvider>
                  </Grid>
                  <Grid item xs={isExtraSmall ? 12 : 6}>
                        <Typography
                                        sx={{
                                          mb: 0.5,
                                          fontWeight: "600",
                                          color: Color.label.main,
                                          fontSize: isMobile ? "12px" : "13px",
                                        }}
                                      >
                                        Order Date (To)
                                      </Typography>
                                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                                        <DatePicker
                                          value={filters.order_date_to}
                                          onChange={(newValue) =>
                                            handleChange({
                                              target: { name: "order_date_to", value: newValue },
                                            })
                                          }
                                          slotProps={{
                                            textField: {
                                              fullWidth: true,
                                              variant: "outlined",
                                              InputLabelProps: { shrink: false },
                                              size: "small",
                                              sx: {
                                                "& .MuiInputLabel-root": { display: "none" },
                                                "& .MuiOutlinedInput-root": {
                                                  "&.Mui-focused fieldset": { borderColor: "#7C0BCC" },
                                                  height: isMobile ? "34px" : "37.69px",
                                                  fontSize: isMobile ? "0.85rem" : "1rem",
                                                },
                                                width: isMobile ? "100%" : 215,
                                              },
                                              error: !!dateErrors.order_date_to,
                                              helperText: dateErrors.order_date_to,
                                            },
                                          }}
                                          format="yyyy-MM-dd"
                                        />
                                      </LocalizationProvider>
                                    </Grid>
                </Grid>
              </Grid>

              <Box
                display="flex"
                flexDirection={isMobile ? "column" : "row"}
                justifyContent="left"
                gap={isMobile ? 1 : 2}
                mt={isMobile ? 2 : 4}
              >
                <Button
                  type="submit"
                  sx={{
                    background: Color.primary.main,
                    color: "white",
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 138,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                    "&:hover": {
                      background: Color.primary.dark,
                    },
                  }}
                  startIcon={<SearchIcon />}
                >
                  Search
                </Button>
                <Button
                  onClick={handleReset}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 138,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Reset
                </Button>
                <Button
                  onClick={() => setDrawerOpen(false)}
                  sx={{
                    background: "none",
                    color: Color.secondary.main,
                    border: "1px solid",
                    borderColor: Color.secondary.main,
                    height: isMobile ? "34px" : "37.69px",
                    width: isMobile ? "100%" : 138,
                    fontSize: isMobile ? "0.85rem" : "1rem",
                  }}
                >
                  Close
                </Button>
              </Box>
            </form>
          </Box>
        </Box>
      </Drawer>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          sx: {
            width: isMobile ? "90vw" : "auto",
            m: isMobile ? 1 : 2,
          },
        }}
      >
        <DialogTitle sx={{ fontSize: isMobile ? "1rem" : "1.25rem" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: isMobile ? "0.85rem" : "1rem" }}>
            Are you sure you want to delete the order <strong>{selectedRow?.order_number}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            mr: 2,
            mb: 2,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 1 : 0,
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              border: "1px solid",
              borderColor: Color.secondary.main,
              color: Color.label.main,
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            sx={{
              background: Color.primary.main,
              color: "white",
              width: isMobile ? "100%" : "auto",
              fontSize: isMobile ? "0.85rem" : "1rem",
              padding: isMobile ? "8px" : "6px 16px",
              "&:hover": {
                background: Color.primary.dark,
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CakeOrderLists;